import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    useTheme, useMediaQuery,
    Box
} from "@mui/material";
import {
    green
} from "@mui/material/colors";
import {
    Save as SaveIcon,
    Done as DoneIcon,
    Preview as PreviewIcon
} from '@mui/icons-material';

import { FilledButton, IconButton } from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";

import { useWorkAndSafetyStore } from "../store";
import { IPrestartStageProps } from "../interfaces/componentInterfaces";


export const PrestartActions = ({
    handleSave
}: IPrestartStageProps) => {
    // ---------------------
    const [atPageEnd, setAtPageEnd] = useState<boolean>(false);

    const {
        editPrestartState
    } = useWorkAndSafetyStore();

    const params = useParams();
    const { jobId, planId, prestartId } = params;
    const isCreate = !baseDefinedAndNotNullNarrowing(prestartId);
    const navigate = useNavigate();

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const handleSaveCallback = useCallback(async () => {
        await handleSave({ ...editPrestartState, status: 'DRAFT' });
    }, [handleSave, editPrestartState]);

    const handleCompleteCallback = useCallback(async () => {
        await handleSave({ ...editPrestartState, status: 'COMPLETED' });
    }, [handleSave, editPrestartState]);

    useEffect(() => {
        // ----------------------------
        const handleScroll = () => {
            // -------------------------------------
            // PAGE END
            if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
                setAtPageEnd(true);
            }
            else if (atPageEnd) {
                setAtPageEnd(false);
            }
        }

        window.addEventListener('scroll', handleScroll);

        return () => {
            // -----------------------------
            window.removeEventListener('scroll', handleScroll);
        }
    });


    return (
        <Box
            position="fixed" bottom={isCreate ? "0px" : (isSmallerThanSm ? "56px" : "70px")} left="0px" right="0px"
            zIndex={1000}
            display="flex" flexDirection="row" justifyContent="center" flex={1}
        >
            <Box
                display="flex" flexDirection="row" justifyContent="center" flex={isSmallerThanSm ? 1 : 0}
                px="10px" py="10px"
                border={atPageEnd ? `2px solid ${green['A700']}` : '1px solid lightgrey'}
                borderRadius="5px"
                bgcolor="white"
            >
                <Box
                    display="flex" flexDirection="row" justifyContent="center" flex={isSmallerThanSm ? 1 : 0}
                    px="15px"
                >
                    {
                        !isSmallerThanSm &&
                        <FilledButton
                            onClick={handleSaveCallback}
                            startIcon={<SaveIcon />}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                        >
                            Save
                        </FilledButton>
                    }
                    {
                        isSmallerThanSm &&
                        <Box display="flex">
                            <IconButton
                                size="small"
                                onClick={handleSaveCallback}
                                tooltipTitle={`Save prestart`}
                                color={theme.palette.secondary.contrastText}
                                backgroundColor={theme.palette.secondary.main}
                                hoverColor={theme.palette.primary.main}
                                hoverBackgroundColor={theme.palette.primary.contrastText}
                            >
                                <SaveIcon />
                            </IconButton>
                        </Box>
                    }
                </Box>
                <Box
                    display="flex" flexDirection="row" justifyContent="center" flex={isSmallerThanSm ? 1 : 0}
                    px="15px"
                >
                    {
                        !isSmallerThanSm &&
                        <FilledButton
                            onClick={handleCompleteCallback}
                            startIcon={<DoneIcon />}
                            backgroundColor="#00c853"
                        >
                            Complete
                        </FilledButton>
                    }
                    {
                        isSmallerThanSm &&
                        <Box display="flex">
                            <IconButton
                                size="small"
                                onClick={handleCompleteCallback}
                                tooltipTitle={`Complete prestart`}
                                color={theme.palette.secondary.contrastText}
                                backgroundColor="#00c853"
                                hoverColor='#00c853'
                                hoverBackgroundColor={theme.palette.primary.contrastText}
                            >
                                <DoneIcon />
                            </IconButton>
                        </Box>
                    }
                </Box>
                {
                    !isCreate &&
                    <Box
                        display="flex" flexDirection="row" justifyContent="center" flex={isSmallerThanSm ? 1 : 0}
                        px="15px"
                    >
                        {
                            !isSmallerThanSm &&
                            <FilledButton
                                onClick={() => {
                                    navigate(`/job/${jobId}/plan/${planId}/view-prestart/${prestartId}`);
                                }}
                                startIcon={<PreviewIcon />}
                                color={theme.palette.secondary.contrastText}
                                backgroundColor={theme.palette.secondary.main}
                            >
                                Preview
                            </FilledButton>
                        }
                        {
                            isSmallerThanSm &&
                            <Box display="flex">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        navigate(`/job/${jobId}/plan/${planId}/view-prestart/${prestartId}`);
                                    }} tooltipTitle={`Preview prestart`}
                                    color={theme.palette.secondary.contrastText}
                                    backgroundColor={theme.palette.secondary.main}
                                >
                                    <PreviewIcon />
                                </IconButton>
                            </Box>
                        }
                    </Box>
                }
            </Box>
        </Box>
    )
}